import { useEffect, useState } from "react";

import type { JobSetting } from "@/lib/jobs";

import { Row } from "./ScheduleEditor";

/**
 * A job's settings — 日本-342, the `Settings` card beside `Schedule` in the job detail view.
 *
 * **Generic over what the server declares.** The spec arrives on the job, so a setting added in
 * `registry.py` renders here with no change on this side — the same relationship `ScheduleEditor`
 * has with `Trigger`, and the reason `JobDefinition.settings` is declared data rather than a field
 * per job. A job with no settings never reaches this component: `JobDetail` draws no card at all
 * rather than an empty one.
 *
 * **Saved on blur and on Enter, never per keystroke.** A number field passes through its
 * neighbours on the way to the value you want — typing `30` over `90` is `3` first — and the server
 * refuses an out-of-range setting with a 400, so a save-as-you-type would reject `3` on a field
 * whose minimum is 10 and fight you mid-edit. That is the same hazard `ScheduleEditor` documents
 * for priority and answers a different way: there the server is lenient, here the editor waits.
 */
export function SettingsEditor({
  settings,
  values,
  onSave,
  onError,
  phone = false,
}: {
  settings: JobSetting[];
  values: Record<string, number>;
  onSave: (values: Record<string, number>) => Promise<unknown> | void;
  onError?: (error: unknown) => void;
  phone?: boolean;
}) {
  // A local draft so a half-typed number is not sent, and so an empty field is a state you can be
  // in. Keyed on the stored VALUES rather than on identity: the job list is replaced on every SSE
  // progress frame, so an identity-keyed effect would reset the field under the cursor several
  // times a second while a job runs.
  const stored = JSON.stringify(values);
  const [draft, setDraft] = useState<Record<string, string>>(() => asText(values));
  useEffect(() => setDraft(asText(JSON.parse(stored) as Record<string, number>)), [stored]);

  const commit = async (setting: JobSetting) => {
    const text = draft[setting.key] ?? "";
    const parsed = Number(text);
    // An empty or unparseable field is not a save — it is a field being edited. Snap back to what
    // is stored rather than sending something the server would only refuse.
    if (text.trim() === "" || !Number.isFinite(parsed)) {
      setDraft(asText(values));
      return;
    }
    const next = Math.round(parsed);
    if (next === values[setting.key]) return;
    try {
      await onSave({ [setting.key]: next });
    } catch (error) {
      // The server's message is written for display, and the field goes back to what is actually
      // stored — leaving a refused number on screen would read as saved.
      setDraft(asText(values));
      onError?.(error);
    }
  };

  const fields = settings.map((setting) => (
    <div key={setting.key} className="flex flex-col gap-[5px]">
      <label className="flex flex-wrap items-center gap-2 text-secondary text-ink-3">
        {setting.label}
        <input
          type="number"
          min={setting.minimum}
          max={setting.maximum}
          value={draft[setting.key] ?? ""}
          aria-label={setting.label}
          onChange={(e) => setDraft({ ...draft, [setting.key]: e.target.value })}
          onBlur={() => void commit(setting)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void commit(setting);
            } else if (e.key === "Escape") {
              setDraft(asText(values));
            }
          }}
          className={[
            "rounded-[var(--r-2)] border border-line-strong bg-panel text-center font-mono text-ink",
            phone
              ? "min-h-[44px] w-[86px] rounded-[var(--r-3)] px-3 text-[17px]/[1]"
              : "w-[72px] px-2.5 py-[7px] text-[15px]/[1]",
          ].join(" ")}
        />
        {setting.unit}
      </label>
      {setting.help && <p className="text-meta text-ink-3">{setting.help}</p>}
    </div>
  ));

  // On the phone the settings are their own card, so they need no label column. On the desktop they
  // sit inside the expanded job row beside the schedule, and share its `SETTINGS`/`SCHEDULE` column
  // — two label columns of slightly different widths in one block is the kind of misalignment
  // nobody can name and everybody sees.
  if (phone) {
    return <div className="flex flex-col gap-[15px] px-[13px] py-[13px]">{fields}</div>;
  }
  return <Row label="設定">{<div className="flex flex-col gap-3">{fields}</div>}</Row>;
}

function asText(values: Record<string, number>): Record<string, string> {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, String(value)]));
}
